/**
 * API 客户端。
 *
 * 会话走 **httpOnly cookie**,前端拿不到也不存令牌 —— 所以这里没有任何
 * localStorage 操作,也不需要手动加鉴权头。`credentials: "include"` 让
 * fetch 带上 cookie;同源部署下其实默认就带,显式写出来是为了换域名时不踩坑。
 */
import type { KnowledgeCategoryRoute, KnowledgePartition, KnowledgeScope, KnowledgeKind, KnowledgeEntryField, KnowledgeFieldKey, KnowledgeProcessingState, KnowledgeReviewState, KnowledgeGapSource, KnowledgeGapStatus, DataScope, PermissionKey, UserRole, CustomerFieldType } from "@usales/contract/values";

export class ApiError extends Error {
  readonly status: number;
  readonly errorCode?: string;
  /** 409(同邮箱存在于多个组织)时带回可选的租户列表。 */
  readonly tenants?: string[];
  constructor(message: string, status: number, tenants?: string[], errorCode?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    if (tenants) this.tenants = tenants;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError(`响应不是 JSON(${res.status}):${text.slice(0, 200)}`, res.status);
  }
  const b = body as { ok?: boolean; error?: string; tenants?: string[]; errorCode?: string };
  if (!res.ok || b.ok === false) {
    throw new ApiError(b.error ?? `请求失败(${res.status})`, res.status, b.tenants, b.errorCode);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  put: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: "PUT", ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  patch: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: "PATCH", ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  del: <T>(path: string) => call<T>(path, { method: "DELETE" }),
};

export const searchKnowledge = (question: string, customerId = "") => api.get<{ hits: SearchHit[] }>(
  `/api/knowledge/search?q=${encodeURIComponent(question)}${customerId ? `&customerId=${customerId}` : ""}`);
export interface KnowledgeAnswer {
  kind: "data" | "knowledge" | "blocked";
  answer: string;
  citations: { documentId: string | null; chunkId?: string; documentTitle: string; partition: string | null; snippet: string }[];
}
export const askKnowledge = (question: string, customerId = "", partition?: KnowledgePartition) => api.post<KnowledgeAnswer>(
  "/api/assistant/ask", { question, intent: "knowledge", ...(customerId ? { customerId } : {}), ...(partition ? { partition } : {}) });

// ── 返回体类型,与 apps/api 的路由一一对应 ──────────────────────

export interface Access {
  tier: UserRole; roleId: string | null; roleName: string;
  permissions: PermissionKey[]; dataScope: DataScope; aiCap: number; departmentId: string | null;
}

export interface Me {
  tenantId: string; userId: string; email: string; name: string;
  role: "sales" | "manager" | "admin";
  /** 角色、权限点、数据范围。菜单按权限点显示;接口那边同样会拦,这里只是不让人看见用不了的入口。 */
  access: Access;
  /** 口令是别人设的、还没改过。为 true 时后端只放行改口令,前端只显示改口令那一屏。 */
  mustChangePassword: boolean;
}

export const can = (me: Pick<Me, "access">, key: PermissionKey): boolean => me.access.permissions.includes(key);

/** 取当前登录用户;未登录返回 undefined(而不是抛),调用方据此决定显示登录页。 */
export async function fetchMe(): Promise<Me | undefined> {
  try {
    const r = await api.get<{ user: Me }>("/api/auth/me");
    return r.user;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return undefined;
    throw e;
  }
}

export async function logout(): Promise<void> {
  await api.post("/api/auth/logout");
}

/** 改自己的口令。成功后其它设备上的登录会失效,当前这个保留。 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post("/api/auth/password", { currentPassword, newPassword });
}

export interface Customer {
  dataQuality?: string;
  id: string; name: string; industry: string | null; region: string | null;
  stage: string; grade: "A" | "B" | "C" | null; currentScore: number | null;
  matchedDims: string[]; lastInteractionAt: string | null;
}

/**
 * 列表行。比 `Customer` 多的是**只有列表要的**几个字段(来源、规模、初筛分、
 * 下一步),它们不在详情接口的基类上 —— 塞进 `Customer` 会让 `CustomerDetail`
 * 继承到详情根本不返回的字段,那种类型说谎只在没人读的时候不炸。
 */
export interface CustomerListRow extends Customer {
  initialVersion:number|null;currentVersion:number|null;
  sizeBand: string | null;
  initialScore: number | null;
  source: string;
  nextActionText: string | null;
}
export interface SalesCustomerList {
  customers: (CustomerListRow & { ownerId: string | null; outcome: "open" | "won" | "lost"; dueOn: string | null; overdueDays: number; followupNote: string; followupChannel: string | null; missingContact: boolean })[];
  total: number;
  stats: { all: number; today: number; overdue: number; untouched: number };
  counts: { all: number; A: number; B: number; C: number };
}

export interface KnowledgeDoc {
  id: string; partition: KnowledgePartition; scope: KnowledgeScope; customerId: string | null; customerName: string | null;
  title: string; chunkCount: number; origin: string; indexedAt: string | null; updatedAt: string;
  entriesState: KnowledgeProcessingState; entriesError: string | null; entryCount: number;
  /** pending:销售上传到团队分区、等主管审核,审核前不参与检索。 */
  reviewState: KnowledgeReviewState; expiresAt: string | null; uploaderName: string | null;
}

/** 资料使用情况(知识引擎 · 效果与沉淀)。 */
export interface KnowledgeUsage {
  days: number;
  cited: { documentId: string; title: string; partition: KnowledgePartition; cards: number; adopted: number; ignored: number; adoptionRate: number | null; lastCitedAt: string }[];
  neverCited: { documentId: string; title: string; partition: KnowledgePartition; createdAt: string }[];
}

/** 知识缺口(#31):助手答不上、动作卡因检索不到知识没出卡时记下的问题。 */
export interface KnowledgeGap {
  id: string; question: string; sources: KnowledgeGapSource[]; askCount: number; askers: string[];
  status: KnowledgeGapStatus; resolvedBy: string | null; resolvedAt: string | null; firstAskedAt: string; lastAskedAt: string;
}

/** 已过有效期:不再被检索到。 */
export const isExpired = (expiresAt: string | null | undefined): boolean => Boolean(expiresAt && new Date(expiresAt).getTime() <= Date.now());

export interface KnowledgeEntry {
  id: string; partition: KnowledgePartition; scope: KnowledgeScope; kind: KnowledgeKind; title: string; summary: string;
  documentId: string; documentTitle: string; sourceChunkId: string; heading: string | null;
  customerId: string | null; customerName: string | null; fields: KnowledgeEntryField[]; evidenceQuote: string; reviewed: boolean; updatedAt: string;
  similarity?: number; mode?: "vector" | "lexical" | "hybrid";
}
export interface KnowledgeEntryResult {
  hits: SearchHit[];
  entries: KnowledgeEntry[]; total: number; sourceDocumentIds: string[];
  facets: { kinds: { key: KnowledgeKind; label: string; count: number }[]; fields: Partial<Record<KnowledgeFieldKey, { value: string; count: number }[]>>; customers: { id: string; name: string }[] };
}

/**
 * 知识类别(`GET /api/knowledge/entry-types`):内置的 + 企业在「系统设置 → 知识库分类」里自加的(k1、k2…)。
 * `archivedAt` 非空 = 停用:AI 不再往里归,只用来给已有条目翻名字。
 */
export interface KnowledgeEntryType {
  id: string | null; key: KnowledgeKind; partition: KnowledgePartition; label: string; description: string;
  builtin: boolean; sort: number; archivedAt: string | null;
}
export const fetchKnowledgeEntryTypes = () => api.get<{ types: KnowledgeEntryType[] }>("/api/knowledge/entry-types").then((r) => r.types);
/**
 * 知识库分类(`GET /api/knowledge/categories`),由企业在「系统设置 → 知识库分类」里自己建。
 * `scope` 建了就不能改,资料是不是客户专属看它;`routes` 是系统资料(复盘、异议沉淀、客户沟通)放进这个分类的那几类。
 */
export interface KnowledgeCategory {
  id: string; key: KnowledgePartition; name: string; description: string; scope: KnowledgeScope; sort: number; routes: KnowledgeCategoryRoute[];
}
export const fetchKnowledgeCategories = () => api.get<{ categories: KnowledgeCategory[] }>("/api/knowledge/categories").then((r) => r.categories);

export interface KnowledgeClassification {
  partition: string; method: "ai" | "manual" | "customer"; reason: string; confidence: number | null;
}
export type KnowledgeUploadResult = { needsCustomer: true; classification: KnowledgeClassification } | {
  needsCustomer?: false; documentId: string; chunkCount: number; embedded: boolean; usedOcr?: boolean;
  executionId: string; undoableUntil?: string; classification?: KnowledgeClassification; reviewState?: KnowledgeReviewState;
};

export interface SearchHit {
  chunkId: string; documentId: string; documentTitle: string; partition: string;
  text: string; heading: string | null; similarity: number;
  /** vector 语义 / lexical 关键词 / hybrid 两条路都命中。 */
  mode: "vector" | "lexical" | "hybrid"; reranked?: boolean;
}

export const SEARCH_MODE_LABEL: Record<SearchHit["mode"], string> = {
  vector: "语义检索", lexical: "关键词检索", hybrid: "语义 + 关键词",
};

export interface ProfileVersion {
  id: string; version: number; cause: string; note: string | null;
  activatedAt: string | null;
  revision: number; createdAt: string; updatedAt: string; createdBy: string | null; creatorName: string | null; baseProfileId: string | null;
  dims: ProfileDim[];
}

/**
 * 企业自定义客户字段的定义(`GET /api/customer-fields`)。值在客户上按 `key` 存(`customFields`),
 * 画像规则与导入映射按 `cf.<key>` 引用;`archivedAt` 非空的只用来把旧值翻成名字,不再可编辑、可筛选。
 */
export interface CustomerFieldDef {
  id: string; key: string; label: string; type: CustomerFieldType;
  options: string[]; aliases: string[]; sort: number; archivedAt: string | null;
}
export interface CustomerFieldImpact {
  field: CustomerFieldDef; customersWithValue: number; canArchive: boolean; signature: string;
  references: { version: number; state: "active" | "draft"; dimensions: string[] }[];
}
export interface StandardCustomerFieldDef {
  key: import("@usales/contract/values").CustomerStandardField; label: string; type: "text"; required: boolean; protected: boolean; archivedAt: string | null;
}
export type StandardCustomerFieldImpact = Omit<CustomerFieldImpact, "field"> & { field: StandardCustomerFieldDef; reason: string | null };
export const fetchCustomerFieldSettings = (includeArchived = true) => api.get<{ fields: CustomerFieldDef[]; standardFields: StandardCustomerFieldDef[] }>(`/api/customer-fields?includeArchived=${includeArchived ? 1 : 0}`);
export type CustomFieldValues = Record<string, string | number>;
export const fetchCustomerFields = (includeArchived = true) =>
  api.get<{ fields: CustomerFieldDef[] }>(`/api/customer-fields?includeArchived=${includeArchived ? 1 : 0}`).then((r) => r.fields);

export interface ImportPreview {
  headers: string[];
  /** 「目标字段 → 资料列名」;标准字段按名字,自定义字段按 `cf.<key>`。 */
  mapping: Record<string, string>;
  /** 本企业未归档的自定义字段,让人能把没对上的列指到某个字段上。 */
  customFields: CustomerFieldDef[];
  standardFields?: StandardCustomerFieldDef[];
  totalRows: number;
  sample: Record<string, string>[];
}

export interface ImportResult {
  batchId: string; totalRows: number; dedupedRows: number; validRows: number;
  highGradeRows: number; invalidRows: number; profileVersion: number | null; elapsedMs: number;
  mergedRows?: number;
}

export type DraftStep =
  | { kind: "question"; question: string; asked: number }
  | {
      kind: "draft"; profileDefId: string; version: number; basis: string;
      autonomyLevel: number; needsConfirmation: boolean;
      draft: { summary: string; dims: { label: string; weight: number; matchRule: string; reason?: string }[] };
    };

// ── M4 业务闭环 ────────────────────────────────────────────────

export interface Citation {
  documentId: string | null; documentTitle: string | null;
  partition: string | null; snippet: string; similarity: string | null;
  /** 资料最近更新时间与有效期:旧资料、已作废资料在卡上要看得出来。 */
  documentUpdatedAt?: string | null; documentExpiresAt?: string | null;
  eventId?: string | null; eventVersion?: number | null; eventSource?: "transcript" | "summary" | null;
  eventOccurredAt?: string | null; currentEventVersion?: number | null;
}

export interface ActionCard {
  id: string; customerId: string; customerName: string;
  grade: "A" | "B" | "C" | null; stage: string;
  actionType: string; channel: string | null; text: string;
  autonomyLevel: number; autonomyReason: string | null;
  status: string; rankScore: string | null; consensusRounds: number;
  createdAt: string;
  citations: Citation[];
  consensus: { id: string; rounds: number; adoptedAt: string | null } | null;
}

export interface ConsensusMessage {
  who: "ai" | "me"; text: string; at: string; stance?: "revised" | "held";
}

export interface ConsensusThread {
  threadId: string; actionCardId: string; rounds: number;
  messages: ConsensusMessage[]; consensusText: string; adoptedAt: string | null;
}

export interface Contact {
  id: string; name: string; decisionRole: string | null;
  phone: string | null; email: string | null; origin: string;
}

export interface CustomerEvent {
  id: string; occurredAt: string; sourceType: string;
  summary: string | null; stageSignal: string | null; durationSec: number | null;
}

export interface CommunicationSignal { value: string; evidence: string; confidence: number; role?: string; phone?: string; comparison?: string }
export interface CommunicationExtraction {
  summary: string; needs: CommunicationSignal[]; objections: CommunicationSignal[]; buyingSignals: CommunicationSignal[]; decisionMakers: CommunicationSignal[]; competitors: CommunicationSignal[];
  stageSignal: { stage: string | null; confidence: number; reason: string; evidence?: string };
}
export interface CommunicationRecord {
  id: string; eventId: string | null; artifactId: string | null; customerId: string; customerName: string; outcome: string; stage: string;
  uploadedByName: string | null; sourceType: string; occurredAt: string; durationSec: number | null; summary: string | null;
  version: number | null; reviewedAt: string | null; status: string; review: string; error: string | null;
  ingestJobId: string | null; taskId: string | null; taskKind: string | null; taskState: string | null; doneSegments: number | null; totalSegments: number | null;
}
export interface CommunicationList {
  records: CommunicationRecord[]; stats: { total: number; ready: number; processing: number; failed: number; pendingReview: number };
  members: { id: string; name: string }[];
}
export interface CommunicationDetail {
  analysisWarning?: string | null;
  record: CommunicationRecord; transcript: string; extraction: CommunicationExtraction | null; sourceAvailable: boolean;
  revisions: { id: string; version: number; kind: string; reason: string; before: CommunicationExtraction | null; after: CommunicationExtraction; actor: string | null; createdAt: string }[];
  taskResult: { note?: string; stage?: { proposed: string; applied: boolean; holdReason?: string; reason: string }; card?: { cardId: string; text: string } | null } | null;
}

export interface Signal {
  kind: string; valueRaw: string; evidenceQuote: string | null; eventId: string;
}

export interface StageTransition {
  id: string; fromStage: string | null; toStage: string; actor: string;
  confidence: string | null; reason: string | null; createdAt: string;
}

export interface CustomerDetail {
  followup: { customerId: string; customerName: string; dueOn: string; overdueDays: number; channel: string; note: string; manuallyArranged: boolean } | null;
  pendingJobId?: string | null;
  customer: Customer & {
    ownerId: string | null; outcome: "open" | "won" | "lost";
    stageManuallySet: string | null; sizeBand: string | null;
    wonAmount: string | null; wonCycleDays: number | null;
    lostPrimaryReason: string | null; lostSecondaryReason: string | null;
    source: string; initialScore: number | null;
    legalName: string | null; address: string | null; registeredCapital: string | null; products: string | null;
    customFields: CustomFieldValues;
    ownerName: string | null; ownerIsYou: boolean; updatedAt:string; canEdit:boolean;
    /** AI 没到门槛的阶段判断,等销售一键确认或忽略;置位、修正后为空。 */
    stageSuggestion: { stage: string; confidence: number; reason: string; eventId: string | null; at: string } | null;
    /** 最近一次出卡尝试的结果,「建议下一步」空着时据此说明原因。 */
    lastCardAttempt: { at: string; eventId: string | null; outcome: "generated" | "no_knowledge" | "no_citation" | "autonomy_l0" | "no_owner"; cardId?: string } | null;
  };
  contacts: Contact[];
  events: CustomerEvent[];
  signals: Signal[];
  transitions: StageTransition[];
  cards: { id: string; text: string; status: string; channel: string | null; autonomyLevel: number; consensusRounds: number; createdAt: string }[];
}

export interface FlywheelReport {
  profile: { fired: boolean; detail: string; profileVersion?: number; activated?: boolean; changes?: { label: string; from: number; to: number }[] };
  knowledge: { fired: boolean; detail: string; documentId?: string; partition?: string; chunkCount?: number };
  autonomy: { fired: boolean; detail: string; backfilled: number; changes: { actionType: string; from: number; to: number; note: string }[] };
}

export interface LossAnalysis {
  primary: string; secondary: string; evidence: string[]; competitor: string; note: string;
  /** 「丢单原因提取」是 L0(只观察):没有调用模型,主因由人自己写。 */
  aiDisabled?: boolean;
}

export interface IngestJob {
  id: string; step: string; eventId: string | null;
  failed: { step: string; kind: string; label: string; advice: string; retryable: boolean; reason: string | null } | null;
  attempts: number; finishedAt: string | null;
  awaitingConfirmation?: string | null; cancelled?: boolean;
  steps: { key: string; label: string; done: boolean; current: boolean; failed: boolean }[];
}

export interface HandoffDraft {
  customerId: string; customerName: string;
  summary: { progress: string; keyPeople: string[]; blockers: string[]; nextStep: string };
  attachments: { events: number; documents: number; contacts: number };
  markdown: string;
}

export interface MessageDraft {
  content: { subject: string; body: string; intent: string };
  delivery: string; deliveryNote: string; warnings: string[]; autonomyLevel: number;
}

export interface TeamUser { id: string; name: string; email: string; role: string }

// ── 管理者控制台 ───────────────────────────────────────────────

export interface ManagerOverview {
  period: string; since: string; until: string;
  newCustomers: number; wonCount: number; lostCount: number; wonAmount: number;
  avgDealSize: number | null; winRate: number | null; avgCycleDays: number | null;
  topLossReasons: { reason: string; count: number }[];
}

export interface FunnelStage {
  stage: string; label: string; current: number; reached: number; toNextRate: number | null;
}

export interface FunnelData {
  stages: FunnelStage[];
  bottleneck: { stage: string; label: string; rate: number; note: string } | null;
  note: string;
}

export interface SalesLoad {
  userId: string; name: string; role: string;
  open: number; won: number; lost: number;
  gradeA: number; gradeB: number; gradeC: number;
  loadRatio: number; overloaded: boolean;
}

export interface AssignmentData {
  capacity: number; unassigned: number; sales: SalesLoad[];
}

export interface RiskItem {
  kind: "cooling" | "untouched" | "downgraded" | "overloaded" | "unassigned";
  label: string; fact: string; suggestion: string;
  customerId?: string; customerName?: string; userId?: string;
}

export interface ReuseData {
  profileFeedback: { version: number; note: string | null; activatedAt: string | null }[];
  lossReasons: { reason: string; count: number }[];
  frequentObjections: { value: string; count: number; customers: number }[];
  retrospectives: { id: string; title: string; partition: string; chunkCount: number; createdAt: string }[];
}

export interface IngestHealthData {
  total: number; finished: number; failed: number; running: number;
  failures: {
    jobId: string; customerId: string; customerName: string;
    step: string; kind: string | null; reason: string | null; attempts: number; at: string;
  }[];
}

export interface UnassignedCustomer {
  id: string; name: string; industry: string | null; region: string | null;
  grade: "A" | "B" | "C" | null; currentScore: number | null; createdAt: string;
}

export interface AssignResult {
  strategy: string; assigned: number;
  unmatched: { customerId: string; customerName: string }[];
  byUser: { userId: string; name: string; count: number }[];
  plan: { customerId: string; customerName: string; toUserId: string; toName: string; reason: string }[];
}

// ── 行动导航的固定区(统计 + 日程)───────────────────────────

export interface MyStats {
  pyramid: { grade: string; count: number }[];
  open: number;
  quoting: number;
  cooling: number;
  coolingDays: number;
  dropped: number;
  negotiating: number;
  wonThisMonth: number;
  wonAmountThisMonth: number;
  lostThisMonth: number;
  revenue: { week: number; month: number; quarter: number; year: number };
  annualTarget: number | null;
  annualPercent: number | null;
  pendingCards: number;
}

export interface ScheduleItem {
  customerId: string; customerName: string;
  grade: "A" | "B" | "C" | null; score: number | null;
  stage: string; stageLabel: string;
  lastInteractionAt: string | null;
  dueOn: string; overdueDays: number; cadenceDays: number;
  predictedCloseOn: string | null; why: string;
  kind: "followup" | "visit" | "meeting" | "prep";
  channel: string | null;
  slot: "am" | "pm";
  canEdit: boolean;
  manuallyArranged: boolean;
  note: string;
}

export interface RecentInteraction {
  customerId: string; customerName: string; stage: string; stageLabel: string;
  lastInteractionAt: string; hasPendingCard: boolean;
}

export interface ScheduleData {
  items: ScheduleItem[];
  /** 三天内刚沟通过的客户,最近的在前。 */
  recent: RecentInteraction[];
  cycleDays: number;
  cycleBasis: "history" | "default";
  overdue: number;
}

export interface CalendarCommunication {
  eventId: string; customerId: string; customerName: string;
  occurredAt: string; sourceType: string; summary: string | null;
}
export interface CalendarCommunicationHistory {
  groups: { date: string; slot: "am" | "pm"; total: number; events: CalendarCommunication[] }[];
}
export interface CalendarCommunicationPage { total: number; events: CalendarCommunication[] }

export interface Member {
  id: string; email: string; name: string;
  role: "sales" | "manager" | "admin"; active: boolean;
  createdAt: string; open: number;
  annualTarget: string | null;
  /** 自定义角色;为空 = 用 role 对应的预置角色。 */
  roleId: string | null; roleName: string | null;
  /** 单独设的数据范围;为空 = 跟角色走(roleDataScope,预置角色见 BUILTIN_ROLES)。 */
  dataScope: DataScope | null; roleDataScope: DataScope | null;
  departmentId: string | null; departmentName: string | null;
}

export interface Department {
  id: string; name: string; parentId: string | null; leaderId: string | null; leaderName: string | null;
  members: number; depth: number;
}

export interface RoleView {
  /** 预置角色是档位名(sales / manager / admin),自定义角色是 uuid。 */
  id: string; name: string; builtin: boolean; baseRole: UserRole;
  permissions: PermissionKey[]; dataScope: DataScope; aiCap: number; description: string | null; members: number;
  /** 预置角色被本企业调整过,可以「恢复默认」。 */
  customized: boolean;
}

// ── 运行记录与每日摘要 ─────────────────────────────────────────

export interface OpsRun {
  id: string; kind: string; ok: boolean; summary: string;
  detail: Record<string, unknown> | null; error: string | null;
  startedAt: string; elapsedMs: number | null;
}

export interface DigestData {
  date: string;
  alerts: { level: "warn" | "error"; text: string }[];
  yesterday: {
    newCustomers: number; ingestOk: number; ingestFailed: number;
    cardsGenerated: number; adopted: number; ignored: number;
    won: number; lost: number; wonAmount: number;
    modelCalls: number; modelCostCny: number; knowledgeGaps: number;
  };
  now: {
    openCustomers: number; unassigned: number; pendingAssignment: number; reserve: number;
    stageSuggestions: number; overdueFollowUps: number; pendingCards: number;
  };
  ops: { kind: string; ok: boolean; summary: string }[];
  text: string;
}

// ── 引擎配置(管理者:录入引擎 / 知识引擎)──────────────────

export interface IngestConfig {
  channels: { key: string; label: string; enabled: boolean; planned: boolean; note: string }[];
  asr: { provider: string; model: string; diarization: boolean; language: string; configured: boolean };
  extraction: { fields: { key: string; label: string; note: string; enabled: boolean }[]; prompt: string | null };
  quality: { accuracy: number | null; samples: number; processedThisWeek: number; needsReview: number };
  recording: { notice: string; rawRetentionDays: number };
}

export interface KnowledgeConfig {
  partitions: {
    key: KnowledgePartition; label: string; description: string; scope: "tenant" | "customer";
    documents: number; chunks: number; entries: number; updatedAt: string | null;
  }[];
  totals: { documents: number; chunks: number; indexed: number; entries: number };
  processing: Record<KnowledgeProcessingState, number>;
  review: { pending: number; expired: number };
  rag: {
    embeddingProvider: string; embeddingModel: string; dimensions: number;
    llmModel: string; topK: number; minSimilarity: number; customerScopeBoost: number;
    minSimilarityIsAuto: boolean; vectorReady: boolean; rerankModel: string | null;
  };
  effect: { adoptionRate: number | null; samples: number; cardsThisMonth: number; traceable: number | null; traceableSamples: number };
}

export interface ProfileDim {
  key: string; label: string; weight: number; matchRule: string; source?: "manual" | "inferred" | "inferred_plus_backfill";
  tiers?: import("@usales/contract/profile-tiers").ProfileTier[];
  backfillable?: boolean; kind?: "rule" | "evidence"; criterion?: string; maxAgeDays?: number; reason?: string;
  basis?: { type: "dialogue" | "customer" | "knowledge"; id?: string; label: string; quote: string }[];
}

/** 动态判定:画像用起来之后,哪些客户的分变了、为什么。 */
export interface ScoreMove {
  id: string; name: string;
  initial: number | null; current: number | null;
  grade: "A" | "B" | "C" | null; dims: string[];
}

/** 可认领的无主客户(原型「＋ 新增 → 🤖 AI推荐」)。 */
export interface Claimable {
  id: string; name: string; industry: string | null; region: string | null;
  grade: "A" | "B" | "C" | null; currentScore: number | null; matchedDims: string[];
}

/** 客户管理顶部总览条。 */
export interface CustomerOverviewData {
  imported: number; valid: number; highGrade: number;
  sales: number; assignedRate: number; unassigned: number;
  /** 备用:C 级、未分配、在跟进 —— 默认不分配,等重新打分。 */
  reserve: number;
  following: number; pendingAssignment: number; settled: number; activeAssignedRate: number | null;
}

/** 名单导入批次(原型 p5「数据导入与清洗」的历史)。 */
export interface ImportBatch {
  id: string; fileName: string; totalRows: number; dedupedRows: number;
  validRows: number; highGradeRows: number;
  status: string; failureReason: string | null; createdAt: string;
}

// ── 报表中心 ─────────────────────────────────────────────────

export interface ReportTemplate {
  id: string; name: string; builtin: "weekly" | "monthly" | null; period: "week" | "month" | "quarter";
  metrics: string[]; auto: boolean; lastGeneratedAt: string | null;
}
export interface ReportKpis {
  won_amount: number; won_count: number; lost_count: number; avg_deal: number | null; win_rate: number | null; avg_cycle: number | null;
  new_customers: number; active_customers: number; communications: number; cards_generated: number; adoption_rate: number | null;
}
export interface ReportSummary {
  id: string; name: string; period: "week" | "month" | "quarter"; range: string; trigger: "auto" | "manual";
  scopeLabel: string; createdAt: string; hasReading: boolean;
}
export interface ReportDetail extends ReportSummary {
  metrics: string[]; periodLabel: string; aiReading: string | null; aiSuggestions: string[]; aiError: string | null;
  data: {
    kpis: ReportKpis; previous: ReportKpis;
    trend?: { start: string; wonAmount: number; wonCount: number }[];
    funnel?: FunnelData;
    ranking?: { userId: string; name: string; wonCount: number; wonAmount: number; lostCount: number; communications: number }[];
    lossReasons?: { reason: string; count: number }[];
  };
}

export interface EnterpriseSetupStatus {
 companyName:string;completedAt:string|null;confirmedCount:number;total:number;nextStep:import("@usales/contract/values").EnterpriseSetupStep;
 steps:{key:import("@usales/contract/values").EnterpriseSetupStep;title:string;description:string;ready:boolean;signature:string;detail:string;state:string;confirmedAt:string|null;changed:boolean}[];
 team:{members:number;mode:"solo"|"team"};profileVersion:number|null;
}
